<template>
  <el-dialog
    :model-value="modelValue"
    title="选择获客链接"
    width="620px"
    append-to-body
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="flex mb-10">
      <template v-if="!isEditing">
        <el-button type="primary" @click="onAdd">添加链接</el-button>
        <el-button type="primary" plain @click="onBatchEdit">批量编辑</el-button>
        <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDeleteSaved">
          删除
        </el-button>
      </template>
      <template v-else>
        <el-button type="primary" plain @click="addRow">增加多条</el-button>
        <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDeleteRow">
          删除
        </el-button>
        <el-button type="primary" :loading="saveLoading" @click="onSave"> 保存 </el-button>
      </template>
    </div>

    <DataTableWithPagination
      v-model:page="page"
      v-model:page-size="pageSize"
      :data="tableRows"
      :loading="loading"
      :total="total"
      :show-column-settings="false"
      highlight-current-row
      padding="0"
      @page-change="loadList"
      @size-change="loadList"
      @selection-change="(rows: FmcAcquisitionLinkRow[]) => (selectedRows = rows)"
      @current-change="
        (row: FmcAcquisitionLinkRow | null) => {
          if (!isEditing) selectedLink = row;
        }
      "
    >
      <el-table-column type="selection" width="44" />
      <el-table-column label="显示标题" width="200">
        <template #default="{ row }">
          <el-input v-if="isRowEditable(row)" v-model="row.title" placeholder="获客标题" />
          <span v-else>{{ row.title }}</span>
        </template>
      </el-table-column>
      <el-table-column label="获客地址">
        <template #default="{ row }">
          <el-input v-if="isRowEditable(row)" v-model="row.address" placeholder="获客地址" />
          <span v-else>{{ row.address }}</span>
        </template>
      </el-table-column>
    </DataTableWithPagination>

    <template #footer>
      <el-button @click="onCancel">取消</el-button>
      <el-button v-if="!isEditing" type="primary" :disabled="!confirmTarget" @click="onConfirm">
        确认
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import DataTableWithPagination from '@/components/DataTableWithPagination.vue';
import type { FmcAcquisitionLinkRow } from '@/types/chatBox';

const props = defineProps<{
  modelValue: boolean;
  accountId?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'confirm', link: Record<string, unknown>): void;
}>();

const loading = ref(false);
const saveLoading = ref(false);
const isEditing = ref(false);
/** 是否批量编辑模式（true 时已保存的行也可编辑；false 时仅新增行可编辑） */
const batchMode = ref(false);
const tableRows = ref<FmcAcquisitionLinkRow[]>([]);

function isRowEditable(row: FmcAcquisitionLinkRow) {
  return isEditing.value && (batchMode.value || !!row.isNew);
}
const selectedRows = ref<FmcAcquisitionLinkRow[]>([]);
const selectedLink = ref<FmcAcquisitionLinkRow | null>(null);

/**
 * 待发送的链接：优先取点击行，其次取勾选。
 * 勾选框本用于批量编辑/删除，但用户看到勾选框就会用它选发送对象，
 * 只认点击行会让「明明勾上了却不能确认」；勾多行时对象不唯一，仍视为未选。
 */
const confirmTarget = computed<FmcAcquisitionLinkRow | null>(
  () =>
    selectedLink.value ??
    (selectedRows.value.length === 1 ? (selectedRows.value[0] ?? null) : null),
);
const total = ref(0);
const page = ref(1);
const pageSize = ref(10);

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      isEditing.value = false;
      batchMode.value = false;
      selectedLink.value = null;
      selectedRows.value = [];
      page.value = 1;
      loadList();
    }
  },
);

async function loadList() {
  loading.value = true;
  try {
    const { channelMaterialsApi, parseMaterialContent } =
      await import('@/api/smartService/channelMaterials');
    const { list, total: t } = await channelMaterialsApi
      .list({
        account_id: props.accountId || '',
        type: 12,
        page: page.value,
        per_page: pageSize.value,
      })
      .catch(() => ({ list: [], total: 0 }));
    tableRows.value = (list as Record<string, unknown>[]).map((r) => {
      const c = parseMaterialContent(r.content);
      return {
        id: r.id as number | undefined,
        title: (c.title as string) || (r.name as string) || '',
        address: (c.url as string) || '',
      };
    });
    total.value = t;
  } finally {
    loading.value = false;
  }
}

function emptyRow(): FmcAcquisitionLinkRow {
  return { title: '', address: '', isNew: true };
}

function onAdd() {
  isEditing.value = true;
  batchMode.value = false;
  selectedRows.value = [];
  tableRows.value.push(emptyRow());
}

function onBatchEdit() {
  isEditing.value = true;
  batchMode.value = true;
  selectedRows.value = [];
}

function addRow() {
  tableRows.value.push(emptyRow());
}

function onDeleteRow() {
  tableRows.value = tableRows.value.filter((r) => !selectedRows.value.includes(r));
  selectedRows.value = [];
}

async function onDeleteSaved() {
  if (selectedRows.value.length === 0) return;
  await ElMessageBox.confirm('确定删除选中的链接吗？', '提示', { type: 'warning' });
  const ids = selectedRows.value.map((r) => r.id).filter((v): v is number => typeof v === 'number');
  const { channelMaterialsApi } = await import('@/api/smartService/channelMaterials');
  await Promise.all(ids.map((id) => channelMaterialsApi.delete(id)));
  ElMessage.success('删除成功');
  loadList();
}

async function onSave() {
  const rows = tableRows.value.filter(
    (r) => isRowEditable(r) && (r.title.trim() || r.address.trim()),
  );
  if (rows.length === 0) {
    ElMessage.warning('请至少填写一条数据');
    return;
  }
  saveLoading.value = true;
  try {
    const { channelMaterialsApi } = await import('@/api/smartService/channelMaterials');
    await Promise.all(
      rows.map((row) => {
        const content = { url: row.address, title: row.title };
        if (row.id) {
          return channelMaterialsApi.update(row.id, { name: row.title || '获客链接', content });
        }
        return channelMaterialsApi.create({
          account_id: props.accountId || '',
          name: row.title || '获客链接',
          type: 12,
          content,
        });
      }),
    );
    ElMessage.success('保存成功');
    isEditing.value = false;
    batchMode.value = false;
    selectedRows.value = [];
    loadList();
  } finally {
    saveLoading.value = false;
  }
}

function onCancel() {
  if (isEditing.value) {
    isEditing.value = false;
    batchMode.value = false;
    loadList();
  } else {
    emit('update:modelValue', false);
  }
}

function onConfirm() {
  if (!confirmTarget.value) return;
  emit('confirm', confirmTarget.value as unknown as Record<string, unknown>);
  emit('update:modelValue', false);
}
</script>
